import React, { useState } from 'react';
import { GestureResponderEvent, View, ViewStyle } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import {
  defaultIconColor,
  IconProps,
  Icons,
  IconSizes,
} from 'shared/components/vxl/icon/Icon/Icon';
import { isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface IconButtonProps
  extends Omit<IconProps, 'iconAccessibilityLabel'>,
    PressableAnalyticsProps {
  /** The function that will be called when the icon is pressed. */
  iconOnPress: (e?: GestureResponderEvent) => void;
  onHover?: () => void;
  onHoverOut?: () => void;
  hoverColor?: string;
  focusedColor?: string;
  /** The accessibility label for the icon. This should always be used in conjunction with ```iconOnPress```.*/
  iconButtonAccessibilityLabel: string;
  /** Disable icon */
  isIconDisabled?: boolean;
  testID?: string;
}

interface ComposableStyle {
  container: ViewStyle;
  iconColor: string;
  focus?: ViewStyle;
  hover?: ViewStyle;
  press?: ViewStyle;
}

const composeIconStyles = (
  defaultColor: string,
  isDisabled: boolean,
  isHovered: boolean,
  isPressed: boolean,
  isFocused: boolean,
  hoverColor?: string,
  focusedColor?: string,
  analyticsId?: string,
): ComposableStyle => {
  if (isDisabled) {
    return {
      iconColor: theme.color.interactive.disabled.text,
      container: {
        borderWidth: 1,
        borderColor: 'transparent',
      },
    };
  } else if (isFocused) {
    return {
      iconColor: focusedColor ?? theme.color.interactive.blackPressed,
      container: {
        borderColor: theme.color.border.focused,
        borderWidth: +(
          analyticsId !== 'passwordMaskClicked' && analyticsId !== 'copyNumber'
        ),
      },
    };
  } else if (isPressed) {
    return {
      iconColor: theme.color.interactive.blackPressed,
      container: {
        borderColor: 'transparent',
        borderWidth: 1,
      },
    };
  } else if (isHovered) {
    return {
      iconColor: hoverColor ?? theme.color.interactive.blackHovered,
      container: {
        borderWidth: 1,
        borderColor: 'transparent',
      },
    };
  }
  return {
    iconColor: defaultColor,
    container: {
      borderWidth: 1,
      borderColor: 'transparent',
    },
  };
};

export const IconButton = ({
  iconKey = 'PLACEHOLDER',
  iconOnPress,
  iconColor = defaultIconColor,
  iconSize = 'MD',
  isIconDisabled = false,
  iconButtonAccessibilityLabel,
  onHover,
  onHoverOut,
  hoverColor,
  focusedColor,
  analyticsId = '',
  customAttributes = null,
  testID = '',
}: IconButtonProps) => {
  const RenderIcon = Icons[iconKey] ?? Icons.PLACEHOLDER;
  const computedSize = IconSizes[iconSize] ?? iconSize;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const computedStyles = composeIconStyles(
    iconColor,
    isIconDisabled,
    isHovered,
    isPressed,
    isFocused,
    hoverColor,
    focusedColor,
    analyticsId,
  );

  const propsForRenderIcon = {
    iconKey,
    iconColor: computedStyles.iconColor,
    iconSize: computedSize,
  };

  const handleHoverOut = () => {
    setIsHovered(false);
    onHoverOut?.();
  };

  const handleHoverIn = () => {
    setIsHovered(true);
    onHover?.();
  };

  return (
    <View testID="iconButtonContainer" style={computedStyles.container}>
      <PressableWithDelay
        analyticsId={analyticsId}
        customAttributes={customAttributes}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onBlur={() => {
          setIsFocused(false);
        }}
        onFocus={() => {
          setIsFocused(true);
        }}
        onPressIn={() => {
          setIsPressed(true);
        }}
        onPressOut={() => {
          setIsPressed(false);
        }}
        onPress={() => {
          iconOnPress();
          if (isWeb) {
            setIsFocused(false);
          }
        }}
        accessibilityLabel={iconButtonAccessibilityLabel}
        accessible
        disabled={Boolean(isIconDisabled)}
        role="button"
        testID={testID}
      >
        <RenderIcon {...propsForRenderIcon} />
      </PressableWithDelay>
    </View>
  );
};

export default IconButton;
